// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

/**
 * Vault and sync lifecycle hooks for the enterprise edition.
 *
 * The endpoints are in the OpenAPI spec (added by D6) and the response types
 * below are the generated ones. The call sites still use raw `fetch` with
 * auth tokens — matching `useExportData` in useStorage.ts — only the types
 * were migrated to codegen, not the client.
 *
 * IMPORTANT: No polling. The vault heartbeat middleware renews the vault
 * key TTL on every authenticated API request, so polling is unnecessary.
 * Instead, vault/sync status is fetched once after login (for cloud users
 * only) and then reactively updated when mutations succeed or API calls
 * return 412/403.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/app/stores/auth-store';
import type { components } from '@flaircv/api-client';

// ── Query keys ──────────────────────────────────────────────

export const VAULT_STATUS_KEY = ['vault-status'] as const;
export const SYNC_STATUS_KEY = ['sync-status'] as const;

// ── Types ───────────────────────────────────────────────────

// Generated from api/openapi.yaml (D6). These were hand-written interfaces
// kept in parallel with the endpoints, which is how `indicator` and
// `needs_storage_reconnect` — the two fields the sync badge reads — came to be
// typed by hand in two files with nothing checking them against the server.
export type VaultStatusResponse = components['schemas']['VaultStatusResponse'];
export type SyncStatusResponse = components['schemas']['SyncStatusResponse'];

// ── Helpers ─────────────────────────────────────────────────

function authHeaders(): HeadersInit {
  const { accessToken } = useAuthStore.getState();
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/** An error from a vault/storage endpoint, carrying the machine-readable code. */
export interface VaultRequestError extends Error {
  httpStatus?: number;
  code?: string;
}

async function vaultFetch<T>(
  url: string,
  opts: RequestInit = {},
): Promise<T> {
  const response = await fetch(url, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...authHeaders(),
      ...(opts.headers ?? {}),
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const error = new Error(
      body?.message ?? body?.error?.message ?? `Request failed (${response.status})`,
    ) as VaultRequestError;
    error.httpStatus = response.status;
    // Endpoints that need the UI to branch answer with a machine-readable
    // `status` (e.g. "backup_missing"). Without carrying it through, the only
    // thing left is the prose message, which the UI would have to parse.
    error.code = typeof body?.status === 'string' ? body.status : undefined;
    throw error;
  }

  return response.json() as Promise<T>;
}

/** Returns true if the user has cloud storage (not platform-only). */
function isCloudStorageUser(): boolean {
  const user = useAuthStore.getState().user;
  const sp = user?.storageProvider;
  return sp === 'gdrive' || sp === 'dropbox';
}

// ── Queries ─────────────────────────────────────────────────

/**
 * Fetch vault status ONCE for cloud storage users.
 * No polling — the vault heartbeat middleware renews TTL on every request.
 * Re-fetched on demand via queryClient.invalidateQueries after mutations.
 */
export function useVaultStatus() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: VAULT_STATUS_KEY,
    queryFn: () => vaultFetch<VaultStatusResponse>('/api/v1/vault/status'),
    enabled: isAuthenticated && isCloudStorageUser(),
    staleTime: Infinity, // Never auto-refetch; only invalidate manually.
  });
}

/**
 * Fetch sync status ONCE for cloud storage users.
 * No polling — status is refreshed on demand after vault mutations.
 */
export function useSyncStatus() {
  const { isAuthenticated } = useAuthStore();

  return useQuery({
    queryKey: SYNC_STATUS_KEY,
    queryFn: () =>
      vaultFetch<SyncStatusResponse>('/api/v1/storage/sync-status'),
    enabled: isAuthenticated && isCloudStorageUser(),
    staleTime: Infinity, // Never auto-refetch; only invalidate manually.
  });
}

// ── Mutations ───────────────────────────────────────────────

/**
 * Unlock the vault by sending the user-derived decryption key.
 * Called after login (password → Argon2id → 32 bytes → base64).
 */
export function useUnlockVault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (decryptionKeyBase64: string) => {
      return vaultFetch<{ status: string; needs_hydration: boolean }>(
        '/api/v1/vault/unlock',
        {
          method: 'POST',
          body: JSON.stringify({ decryption_key: decryptionKeyBase64 }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VAULT_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
    },
  });
}

/**
 * Lock the vault (removes key from Redis).
 */
export function useLockVault() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return vaultFetch<{ status: string }>('/api/v1/vault/lock', {
        method: 'POST',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VAULT_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
    },
  });
}

/**
 * Set up a vault passphrase (for OIDC users who don't have a password).
 * Sends both the plaintext passphrase (for bcrypt hashing server-side)
 * and the derived decryption key (for vault unlock).
 */
export function useSetupVaultPassphrase() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: {
      passphrase: string;
      decryptionKeyBase64: string;
    }) => {
      return vaultFetch<{ status: string }>(
        '/api/v1/vault/setup-passphrase',
        {
          method: 'POST',
          body: JSON.stringify({
            passphrase: params.passphrase,
            decryption_key: params.decryptionKeyBase64,
          }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VAULT_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
    },
  });
}

/**
 * Trigger hydration from cloud storage.
 * Downloads encrypted blob → decrypts → hydrates PostgreSQL.
 */
export function useHydrateFromCloud() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return vaultFetch<{ status: string }>('/api/v1/storage/hydrate', {
        method: 'POST',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: VAULT_STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: SYNC_STATUS_KEY });
      // Invalidate all data queries so the UI picks up hydrated data.
      queryClient.invalidateQueries();
    },
  });
}
